/**
 * AiAnalyticsCards — what the AI has seen today, as a row of cards that appears and disappears
 * with the models this node actually runs.
 *
 * The point is that an operator gets the shape of the day without opening a single report: how
 * many people were recognised, how many are on site, whether anything is burning. Every figure
 * comes from ONE call to the node's /api/analytics/summary, so this component is purely
 * presentational — same contract as NodeSummaryCards, which it renders beside.
 *
 * ── Two rules it exists to enforce ──
 *
 * 1. NEVER present a detection count as a count of people. One person in front of a camera for
 *    thirty seconds produces dozens of events, so "1,284 people detected" would be wrong by a
 *    factor of fifty and would cost the reader their trust in every other number here. Where a
 *    real headcount exists it is used and labelled as one (recognised PEOPLE, on site NOW);
 *    everywhere else the figure is labelled "detections" and means exactly that.
 *
 * 2. An active model with nothing to report shows ZERO, it does not vanish. A missing card is
 *    indistinguishable from a broken pipeline, and "no intrusions today" is the single most
 *    useful thing an intrusion detector can tell you.
 *
 * 3. EVERY FIGURE THAT HAS A SHAPE SHOWS IT. A count alone cannot distinguish six intrusions
 *    spread across the day from six in the last twenty minutes, so each card carries the hourly
 *    series behind its headline — a sparkline where the trend is the point, bars where each hour
 *    is, a gauge where the question is "how much of the whole". The marks live in MiniCharts and
 *    are fed by the same /analytics/summary payload as the numbers above them, so a chart can
 *    never disagree with its own figure.
 *
 * Alerts get TWO cards rather than one. "How much is outstanding" and "when did it happen" are
 * different questions with different windows — the first is the standing position at any age, the
 * second a rolling 24 hours so a morning shift can see the night — and a single tile answering
 * both ends up answering neither.
 *
 * NO ALERT BAND ABOVE THE ROW any more. This file carried one for a long time — fire & smoke
 * first, then criticals, then every severity — and each version was the same wager: that "what
 * is open right now" is worth a strip of its own above the counts. It is, and that strip is the
 * Alerts dashboard panel, which shows the alerts themselves as pictures with a timeline under
 * them. A second band restating it from the summary put two answers to one question on one
 * screen, and the shorter of the two was the one duplicating. What is left here is what this
 * sheet is for: the standing counts, and the newest few as a list inside the Alerts card.
 */
import type { ComponentType, ReactNode } from 'react';
import {
  FaceSmileIcon,
  UserIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  TruckIcon,
  HandRaisedIcon,
  ArrowTrendingDownIcon,
  FireIcon,
  BellAlertIcon,
  UserGroupIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { MiniCard, formatAlertTime } from './NodeSummaryCards';
import { alertSeverityVar, SeverityTag } from '../alerts/AlertTags';
import { alertName } from '../alerts/alertVocab';
import { Avatar } from '../alerts/Avatar';
import {
  AxisRow,
  Gauge,
  MiniBars,
  SegmentedBar,
  Sparkline,
  bucketLabel,
  hasSeries,
  seriesMax,
  type Series,
} from './MiniCharts';

// ─── Payload (mirrors services/analyticsSummaryService.js) ───────────────────

export interface AiSummaryWindow {
  from: number;
  to: number;
  label: string;
}

/** Per-surface shapes. Every one carries `events`; the rest depends on what the model knows. */
export interface AiSurfaceSummary {
  events: number;
  /**
   * Hourly buckets across the window — the card's chart. `null` where the series query failed
   * on a surface whose figures survived, which is why it is nullable independently of the rest.
   */
  series?: Series | null;
  /** Faces / falls — DISTINCT identified subjects, which is a headcount. */
  subjects?: number;
  known?: number;
  unknown?: number;
  /** person-count — a real occupancy reading, summed across every counting camera. */
  now?: number;
  peak?: number;
  reporting?: number;
  /**
   * Which cameras that total is made of, busiest first — capped server-side.
   *
   * Carries ids, not names: the events table is in the command-center's database and camera names
   * are in the node backend's, so the caller resolves them through `cameraNames`.
   */
  by_camera?: Array<{ camera_id: string; now: number; peak: number }> | null;
  withPlate?: number;
  fire?: number;
  smoke?: number;
  latest?: { id?: string; camera_id?: string; detection_type?: string; event_timestamp?: number } | null;
  /**
   * fire-smoke only — how many of today's detections actually RAISED an alert, and how many of
   * those were critical.
   *
   * A detection count can't answer "does anyone need to move": the detector fires on a hazy
   * camera the same way it fires on a fire, and it is the alert — a rule somebody wrote, with a
   * severity somebody chose — that separates the two.
   */
  alerts?: { total: number; critical: number } | null;
  /**
   * fire-smoke only — the newest alert this detector raised today: what it is called (the
   * matched rule's name), where, how bad, and when. Absent on a node that predates the field,
   * which is why the banner falls back to `latest`.
   */
  latestAlert?: {
    id?: string;
    camera_id?: string;
    severity?: string;
    name?: string | null;
    event_timestamp?: number | null;
  } | null;
  /** breakdownBy() surfaces — `{ 'person': 12, 'vehicle': 3 }`. */
  by?: Record<string, number>;
}

export interface AiAlertSummary {
  open: { total: number; critical: number; warning: number; normal: number; unacked: number };
  today: { total: number; critical: number; warning: number; normal: number };
  /**
   * The newest still-OPEN critical alert — what the rule calls it, where, and when.
   *
   * NOT DRAWN by this row any more — the band that used to render it has moved to the Alerts
   * dashboard panel, which lists the alerts themselves. Kept on the contract because the summary
   * endpoint still returns it and a caller with no alert feed of its own has nothing else to
   * name the newest critical with; deleting the field would only mean putting it back.
   */
  latestCritical?: {
    id?: string;
    camera_id?: string;
    status?: string;
    name?: string | null;
    event_timestamp?: number | null;
  } | null;
  /**
   * A ROLLING window, unlike everything else here — an operator arriving at 08:00 needs to see
   * the night, which a since-midnight chart would have thrown away.
   *
   * NOT DRAWN by this row any more: the Alert Activity card that owned it was merged into the
   * Alerts card, which answers "when" with the four dated rows at its foot instead of a
   * sparkline. Kept on the contract because the summary endpoint still returns it and the alert
   * pages are the right home for a chart of the day — deleting the field would only mean
   * putting it back.
   */
  activity?: { hours: number; from: number; to: number; total: number; series: Series } | null;
}

/** One row of the presence card's arrivals list. */
export interface AiArrival {
  id?: string;
  name: string;
  /** Designation, or the department when there is no designation. */
  role?: string | null;
  at: number | null;
  /** Still on site. `false` means they came and went — see `left_at`. */
  inside?: boolean;
  left_at?: number | null;
}

export interface AiAttendanceSummary {
  work_date: string;
  /** Who came in last, newest first. `at` and `left_at` are epoch SECONDS. */
  arrivals?: AiArrival[] | null;
  total: number;
  present: number;
  inside: number;
  absent: number;
  short_day: number;
}

export interface AiAnalyticsSummary {
  window: AiSummaryWindow;
  /** `null` for a surface whose query failed — which is NOT the same as zero. */
  surfaces: Record<string, AiSurfaceSummary | null>;
  alerts: AiAlertSummary | null;
  attendance: AiAttendanceSummary | null;
  generated_at?: string;
  cached?: boolean;
}

/** Where a card goes when clicked. Omit a key and that card is a plain panel, not a button. */
export interface AiAnalyticsLinks {
  /** surface key → route, e.g. `{ face: '/reports/face' }`. */
  reports?: Record<string, string>;
  alerts?: string;
  attendance?: string;
}

export interface AiAnalyticsCardsProps {
  /**
   * The newest alerts, newest first — the list that used to be its own card in the summary row.
   *
   * It lives here now because an alert IS an analytics result: the row above answers "what did
   * the AI see", and "what did it decide was worth waking someone for" is the same question one
   * step on. Drawn as the foot of the Alerts card rather than a tile of its own — the counts
   * above say how much, these say what.
   */
  latestAlerts?: any[];
  /**
   * camera id → display name, for the alert lines. Alerts are denormalised for query speed and
   * carry a `camera_id` and no name (migration 008), so the name has to come from the caller —
   * which already holds the camera list for the cards beside this one. Unknown ids simply lose
   * the location half of the line rather than printing a uuid at an operator.
   */
  cameraNames?: Record<string, string>;
  /**
   * Active model surfaces, or `null` while the registry is unknown (see
   * useActiveModelSurfaces). Null renders the alert and presence cards only, plus a quiet note
   * — better than guessing at a model list and drawing cards for detectors nobody runs.
   */
  surfaces: string[] | null;
  summary: AiAnalyticsSummary | null;
  loading?: boolean;
  /**
   * Whether the PER-MODEL figures are still coming, when they arrive separately from the
   * alert and presence ones.
   *
   * They do on a dashboard, and the reason is a dependency the row can't see: the per-model
   * request has to name the surfaces this node runs, so it waits on the model registry — a
   * second round trip, to a different service — while the alert and presence figures need no
   * such thing and could have been on screen a beat after the page opened. One shared flag
   * held the whole row in skeletons for the slowest of the two. Defaults to `loading`, so a
   * caller that fetches everything in one request says nothing and behaves as before.
   */
  surfacesLoading?: boolean;
  links?: AiAnalyticsLinks;
  onNavigate?: (to: string) => void;
  /**
   * Extra cards to place at the END of this grid — the node's own tiles (cameras, models, machine
   * health).
   *
   * They live in THIS grid rather than a second one below it because two stacked grids align
   * independently: the analytics row would end wherever its cards ran out and the machine row
   * would start a fresh line, reading as two blocks that happen to touch. One grid means one set
   * of row lines. Pass `<NodeSummaryCards bare />` so they arrive as cells, not wrapped in a grid.
   */
  trailing?: ReactNode;
  /**
   * Render at most this many of THIS component's cards, dropping the rest entirely.
   *
   * For the estate view, where a site's row is one of many on a page meant to be scanned: the
   * first few cards answer "does this site need me" and the rest are detail the reader asks for by
   * expanding. Dropped, not hidden — a `display:none` card is still mounted, and ten sites' worth
   * of unseen charts is real DOM for nothing.
   *
   * The cap trims from the END of the priority order, so it always keeps alerts, then how they
   * trended, then who is on site, then the models. `trailing` belongs to the caller, which
   * decides whether to pass any while collapsed.
   */
  limit?: number;
  className?: string;
}

// ─── Card parts ──────────────────────────────────────────────────────────────

/**
 * What a renderer knows besides its own figures. Only the caller can turn a camera id into a
 * name, so anything a card wants to say about WHERE a number came from arrives here.
 */
interface CardContext {
  cameraNames?: Record<string, string>;
}

type Renderer = (s: AiSurfaceSummary, ctx: CardContext) => ReactNode;

/**
 * The section has exactly TWO kinds of card, and the difference is what they carry:
 *
 *   big    model state PLUS a log — the counts and then the things themselves. Alerts and its
 *          most-recent line, Alert Activity and its newest four, Presence and who just arrived,
 *          Person Count and which cameras it is the sum of. One per grid cell.
 *   small  model state ONLY — a figure and its shape over the day, nothing else. TWO per grid
 *          cell, stacked, so a pair stands as tall as one big card.
 *
 * The pairing is the point. A state-only card is about half a big card's content, and on its own
 * in a stretched grid row it is half a card of emptiness. Two of them share one cell instead, and
 * the row reads as full without anything being padded out.
 *
 * This is also why a small card carries no list: the moment it does, it stops fitting half a cell
 * and the layout has to choose between a ragged row and a void. A caveat that must be said but
 * needn't be read every time goes on a tooltip (see `Figure`'s `hint`).
 */
type CardSize = 'big' | 'small';

interface SurfaceCard {
  title: string;
  icon: ComponentType<{ className?: string }>;
  size: CardSize;
  render: Renderer;
}

const nf = new Intl.NumberFormat();
const n = (v: number | undefined) => nf.format(Number(v || 0));

/**
 * Series colour by what the figure MEANS.
 *
 * `--info`, `--success`, `--warning` and `--destructive` are the product's reserved status
 * vocabulary — they are what a severity pill is painted with — so a chart may only wear one when
 * its subject genuinely is that state. An intrusion count is bad news by definition and gets the
 * destructive red; a count of people walking past a camera is not a state at all, and gets the
 * brand ink rather than borrowing "info" blue and implying one.
 */
const SERIES_BRAND = 'var(--brand)';
const SERIES_QUIET = 'var(--muted-foreground)';
/**
 * The detections series is blue, by request.
 *
 * It is `--info`, the theme's blue, so it restyles with everything else rather than being a hex
 * dropped into a component. Worth knowing: that token is also the "info" severity in the alert
 * bar two cards up. The reuse is tolerable here because the two never sit on the same mark and
 * each is labelled in words — but it is a borrowed token, not a series colour of its own, and a
 * third use would be the point to give the palette a real categorical slot.
 */
const SERIES_BLUE = 'var(--info)';

/**
 * The card's headline number and what it MEANS. `unit` is not decoration — it is the difference
 * between a figure a reader can act on and one that quietly misleads them.
 *
 * Proportional figures, deliberately not `tabular-nums`: equal-width digits are for columns that
 * line up vertically, and at this size they make a number like `121` read as loose.
 */
function Figure({
  value,
  unit,
  tone,
  /** Put the unit on its own line — for a phrase ("need attention") rather than a noun. */
  block = false,
  hint,
}: {
  value: ReactNode;
  unit: string;
  tone?: string;
  block?: boolean;
  /**
   * The caveat behind the unit, on hover. For a fact that changes how the figure should be read
   * but does not need to be re-read every time it is seen — a small card has no line to spare
   * for it, and dropping it entirely would leave the number free to mislead.
   */
  hint?: string;
}) {
  const figure = (
    <span
      className="text-[26px] font-bold leading-none tracking-tight"
      style={tone ? { color: tone } : undefined}
    >
      {value}
    </span>
  );
  if (block) {
    return (
      <div className="flex-none">
        {figure}
        <p className="mt-1 text-[11px] leading-tight text-muted-foreground" title={hint}>
          {unit}
        </p>
      </div>
    );
  }
  return (
    <div className="flex flex-none items-baseline gap-1.5">
      {figure}
      <span
        className={`min-w-0 truncate text-[11px] text-muted-foreground ${hint ? 'decoration-dotted underline-offset-2 hover:underline' : ''}`}
        title={hint}
      >
        {unit}
      </span>
    </div>
  );
}

/** A small labelled row under the headline. */
function Sub({ items }: { items: Array<[label: string, value: ReactNode]> }) {
  return (
    <div className="mt-2.5 space-y-1 border-t border-border pt-2">
      {items.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-2 text-[11px]">
          <span className="min-w-0 truncate text-muted-foreground">{label}</span>
          <span className="flex-none font-medium tabular-nums text-foreground">{value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * A card with nothing in it, said properly.
 *
 * A quiet card is not a broken card, but in a stretched grid row it looked like one. Cards are
 * as tall as the tallest in their row — and since the alerts card grew a list of four, that is
 * tall — so a detector that saw nothing had a figure, one grey sentence adrift in the middle of
 * it, and a hundred-odd pixels the reader had to decide about. Left at the bottom the space read
 * as content that failed to load; centred, as a card missing its middle.
 *
 * A dashed well takes that space DELIBERATELY. The card is the same height either way; the
 * difference is that the height now carries the message — this is empty, and it is empty on
 * purpose — instead of being the absence of one. It grows and shrinks with whatever slack the
 * row hands it, so it works at any disparity.
 *
 * A tick rather than a warning glyph: on a security dashboard, nothing detected is the good
 * outcome, and it should not be dressed as a fault.
 */
function Quiet({ children }: { children: ReactNode }) {
  return (
    <div className="mt-2 flex min-h-[52px] flex-1 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border px-2 py-2 text-center">
      <CheckCircleIcon className="h-4 w-4 flex-none text-muted-foreground/50" aria-hidden />
      <p className="text-[11px] leading-tight text-muted-foreground">{children}</p>
    </div>
  );
}

/** `by` breakdowns as chips — the top few, because a card is not a report. */
function Chips({ by }: { by?: Record<string, number> }) {
  const entries = Object.entries(by || {}).filter(([, v]) => v > 0).slice(0, 4);
  if (entries.length === 0) return null;
  return (
    <div className="mt-2.5 flex flex-wrap gap-1 border-t border-border pt-2">
      {entries.map(([k, v]) => (
        <span
          key={k}
          className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
          title={`${k}: ${n(v)}`}
        >
          {k} <span className="tabular-nums text-foreground">{n(v)}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Where the occupancy total comes from, camera by camera, busiest first.
 *
 * Only cameras that reported in the last few minutes are here — the same freshness window `now`
 * is built from, because a camera that went quiet at lunchtime must not keep contributing a
 * headcount. That is also why the header states the count separately: the list can be shorter
 * than the number of counting cameras (the payload caps it), and a site with the model attached
 * to nothing at all has to read as "no cameras counting" rather than as an empty space.
 */
function CameraSplit({
  cameras,
  reporting,
  names,
}: {
  cameras?: Array<{ camera_id: string; now: number; peak: number }> | null;
  reporting: number;
  names?: Record<string, string>;
}) {
  if (reporting === 0) {
    return (
      <p className="mt-2 border-t border-border pt-2 text-[10px] leading-snug text-muted-foreground">
        No camera has reported a count in the last few minutes.
      </p>
    );
  }
  if (!cameras || cameras.length === 0) return null;

  return (
    <div className="mt-2.5 border-t border-border pt-2">
      <Caption>
        By camera · {n(reporting)} counting
      </Caption>
      <ul className="space-y-1">
        {cameras.map((c) => (
          <li
            key={c.camera_id}
            className="flex items-center gap-1.5 text-[11px]"
            // The camera's own high-water mark today, on hover: it is a per-camera figure and the
            // card's own "Peak today" is the site's, so putting both on the row would read as a
            // contradiction. Reachable, not asserted.
            title={`${names?.[c.camera_id] || c.camera_id} · ${n(c.now)} now, peak ${n(c.peak)} today`}
          >
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {/* A uuid is not a place. Where the name can't be resolved, a short prefix at
                  least distinguishes one unnamed camera from another, which "Unknown" would not. */}
              {names?.[c.camera_id] || `#${c.camera_id.slice(0, 8)}`}
            </span>
            <span className="flex-none font-medium tabular-nums text-foreground">{n(c.now)}</span>
          </li>
        ))}
        {reporting > cameras.length && (
          <li className="text-[10px] text-muted-foreground">
            +{n(reporting - cameras.length)} more
          </li>
        )}
      </ul>
    </div>
  );
}

/** The heading on a card's second half — what the block beneath it is a list OF. */
function Caption({ children }: { children: ReactNode }) {
  return (
    <p className="mb-1.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{children}</p>
  );
}

/**
 * A chart, wrapped in the two lines that make it readable.
 *
 * ── Why both lines are mandatory ──
 *
 * ABOVE: what the marks measure, and the value the tallest of them represents. A bar row on its
 * own is a shape with no scale — you can see that three o'clock was busier than four and you
 * cannot tell whether the tallest bar is four people or forty. Worse, on a card whose headline
 * reads "1 person" a chart peaking at 4 looks like it contradicts the figure, when in fact one
 * is now and the other is the whole day. `caption` names the quantity, `scale` anchors it.
 *
 * BELOW: where in time the marks sit. Three clock readings — first bucket, middle, now — so a bar
 * can be placed on the day without hovering it.
 */
function Plot({
  children,
  caption,
  scale,
  left,
  center,
  right,
}: {
  children: ReactNode;
  /** What one mark measures, e.g. "Occupancy per hour". */
  caption?: ReactNode;
  /** The value the tallest mark stands for, e.g. "peak 4". */
  scale?: ReactNode;
  left?: ReactNode;
  center?: ReactNode;
  right?: ReactNode;
}) {
  return (
    // The chart is where a card's spare height GOES.
    //
    // Cards in a grid row are all as tall as the tallest one, and their content is not. Left to
    // pool, that difference is a void at the bottom; spread between blocks, it is a gap in the
    // middle. Given to the plot, it is more chart — the one thing on the card that is better
    // bigger. `max-h` stops that becoming absurd: a 200px-tall sparkline for three detections
    // reads as a stretched mark rather than a considered one, so past that point the card keeps
    // the remainder as ordinary breathing room.
    <div className="mt-2.5 flex min-h-0 flex-1 flex-col">
      {(caption || scale) && (
        <div className="mb-1 flex flex-none items-baseline justify-between gap-2 text-[10px] leading-none">
          <span className="min-w-0 truncate text-muted-foreground">{caption}</span>
          <span className="flex-none font-semibold tabular-nums text-foreground">{scale}</span>
        </div>
      )}
      <div className="min-h-[34px] max-h-[104px] flex-1">{children}</div>
      <AxisRow left={left} center={center} right={right} />
    </div>
  );
}

/**
 * Today's chart, with its time axis labelled from the series itself.
 *
 * The middle tick is dropped on a short series: at three buckets the middle label sits close
 * enough to the ends to read as a collision rather than as a scale.
 */
function TodayPlot({
  series,
  caption,
  scale,
  children,
}: {
  series: Series;
  caption?: ReactNode;
  scale?: ReactNode;
  children: ReactNode;
}) {
  const mid = series.values.length >= 5 ? bucketLabel(series, Math.floor((series.values.length - 1) / 2)) : undefined;
  return (
    <Plot caption={caption} scale={scale} left={bucketLabel(series, 0)} center={mid} right="now">
      {children}
    </Plot>
  );
}

/**
 * First letter up. The unit nouns are written to follow a figure ("3 detections"), not to open a
 * caption.
 */
const sentence = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/**
 * A detector card: the count, its shape over the day, and whatever breakdown it carries.
 *
 * Every model that has no richer story to tell renders through this, so the sparse detectors read
 * as one family instead of each inventing its own layout.
 */
function detector({
  unit,
  unitOne,
  tone,
  extra,
}: {
  unit: string;
  unitOne?: string;
  /** The colour this detector's bad news wears. */
  tone: string;
  /**
   * The card's tail — chips, a sub-row. Takes the figures only, deliberately not the full
   * `Renderer` signature: a breakdown of a detector's own columns has no business needing to
   * know a camera's name, and widening it would invite exactly that.
   */
  extra?: (s: AiSurfaceSummary) => ReactNode;
}): Renderer {
  return (s) => (
    <>
      <Figure
        value={n(s.events)}
        unit={s.events === 1 ? unitOne || unit : unit}
        tone={s.events > 0 ? tone : undefined}
      />
      {/* Zero gets NO chart. The figure above already says zero and says it clearly; a flat
          line with a marker on its end reads as a stray divider rather than as a quiet day,
          and it costs the card the only vertical space its breakdown has. */}
      {s.events > 0 && hasSeries(s.series) && (
        <TodayPlot
          series={s.series}
          caption={`${sentence(unit)} per hour`}
          scale={`max ${n(seriesMax(s.series))}`}
        >
          <Sparkline series={s.series} color={tone} ariaLabel={`${unit} per hour today`} />
        </TodayPlot>
      )}
      {s.events === 0 && <Quiet>Nothing detected today</Quiet>}
      {extra?.(s)}
    </>
  );
}

// ─── Card catalogue ──────────────────────────────────────────────────────────

/**
 * surface key → card. Order is the order they read: who was here, how many, then what went
 * wrong — so the safety detectors sit together at the end rather than being scattered.
 *
 * `fire-smoke` is absent by design; it is the banner.
 */
const SURFACE_CARDS: Record<string, SurfaceCard> = {
  face: {
    title: 'Faces Recognised',
    icon: FaceSmileIcon,
    size: 'small',
    // The headline is PEOPLE (distinct matches), not detections — the same face seen twenty
    // times is one person, and that is the number an operator means by "how many did we know".
    // The gauge below it is about DETECTIONS and says so in its legend: the two are different
    // quantities and the card must not let one be read as the other.
    render: (s) => {
      const known = Number(s.known || 0);
      const unknown = Number(s.unknown || 0);
      const seen = known + unknown;
      return (
        <>
          <Figure value={n(s.subjects)} unit={s.subjects === 1 ? 'person' : 'people'} />
          {/* Sized for half a cell: the ring steps down from 74 so the card, its figure and its
              legend clear a stacked pair without the gauge having to be cropped. */}
          <div className="mt-2 flex min-h-0 flex-1 items-center">
            <Gauge
              percent={seen > 0 ? (known / seen) * 100 : 0}
              caption="matched"
              color="var(--success)"
              size={58}
              ariaLabel={`${known} of ${seen} face detections matched a registered person`}
              legend={[
                { key: 'known', label: 'Known', value: n(known), color: 'var(--success)' },
                { key: 'unknown', label: 'Unrecognised', value: n(unknown), color: 'var(--warning)' },
              ]}
            />
          </div>
        </>
      );
    },
  },
  person: {
    title: 'People Detected',
    icon: UserIcon,
    size: 'small',
    // Deliberately "detections": person events have no identity, so this cannot be a headcount.
    // The occupancy card beside it is the one that answers "how many people".
    render: (s) => (
      <>
        {/* The caveat moved from a two-line footnote to the unit's tooltip. A state-only card has
            no room for it, and the word "detections" is doing the load-bearing part of the job:
            it is already not a count of people. */}
        <Figure
          value={n(s.events)}
          unit="detections"
          hint="Sightings, not people — one person may be detected many times."
        />
        {hasSeries(s.series) && (
          // `last`, not `peak`: on a sightings chart the question is whether the cameras are
          // seeing anything RIGHT NOW, and the busiest hour is the one a reader spots unaided.
          <TodayPlot
            series={s.series}
            caption="Detections per hour"
            scale={`max ${n(seriesMax(s.series))}`}
          >
            <MiniBars series={s.series} color={SERIES_BLUE} emphasis="last" unit="detections" />
          </TodayPlot>
        )}
        {s.events === 0 && <Quiet>Nothing detected today</Quiet>}
      </>
    ),
  },
  /**
   * ── Person Count ──
   * Named for the MODEL, the same way the report it links to is (reportModels.ts: "Person
   * Count"). It was "On Site Now", which described one figure on the card rather than what
   * produced any of them — an operator looking for what their person-counting model had to say
   * had no card with that name on it, and the title gave no clue that the tile next to it
   * ("People Detected") is a different model answering a different question.
   */
  'person-count': {
    title: 'Person Count',
    icon: UsersIcon,
    size: 'big',
    render: (s, ctx) => (
      <>
        {/* "on site now", not "people": the headline is the CURRENT level, while everything
            below it is the whole day, and the two get confused the moment the unit stops saying
            which one this is. */}
        <Figure value={n(s.now)} unit="on site now" tone={SERIES_BRAND} />
        {hasSeries(s.series) && (
          // A STEP LEVEL, not bars. Occupancy is a reading that persists: four people in a room
          // at 14:00 are the same four at 14:30 unless something changed, and a bar per hour
          // draws that as four separate events — it reads like a count of arrivals, which is the
          // card beside this one. The step line holds each hour's reading until the next and
          // moves in one jump, so a flat run reads as "nobody came or went" and the shape of the
          // day is a level rising and falling rather than a row of tallies.
          //
          // "Site total" in the caption is load-bearing: the model is normally on several
          // cameras and every figure on this card is their SUM, so a reader must not take the
          // line for one camera's room.
          <TodayPlot
            series={s.series}
            caption="Site total, highest each hour"
            scale={`max ${n(seriesMax(s.series))}`}
          >
            <Sparkline
              series={s.series}
              color={SERIES_BRAND}
              step
              ariaLabel="total occupancy across all counting cameras, by hour"
            />
          </TodayPlot>
        )}
        {/* No "Peak today" row: `peak` is now defined as the tallest hour of the series, so the
            chart's own `max` above IS that number. Printing it twice invited the reader to look
            for the difference between two figures that cannot differ. */}
        {/* WHICH cameras the total is made of. "6 on site" is not checkable; "2 at the main gate,
            2 in the lobby, 2 on the dock" is — and it is the only thing on the card that tells a
            reader the model is attached to more than one camera at all. */}
        <CameraSplit cameras={s.by_camera} reporting={Number(s.reporting || 0)} names={ctx.cameraNames} />
        {/* A site that has been empty all day draws no step line and has no cameras to split
            between — the card would be a zero over a void. */}
        {!hasSeries(s.series) && !(s.by_camera && s.by_camera.length) && (
          <Quiet>Nobody counted on site today</Quiet>
        )}
      </>
    ),
  },
  intrusion: {
    title: 'Intrusions',
    size: 'small',
    icon: ExclamationTriangleIcon,
    render: detector({
      unit: 'detections',
      unitOne: 'detection',
      tone: 'var(--destructive)',
      extra: (s) => <Chips by={s.by} />,
    }),
  },
  'ppe-violation': {
    title: 'PPE Violations',
    size: 'small',
    icon: ShieldCheckIcon,
    render: detector({
      unit: 'violations',
      unitOne: 'violation',
      tone: 'var(--warning)',
      extra: (s) => <Chips by={s.by} />,
    }),
  },
  'vehicle-crossing': {
    title: 'Vehicles',
    size: 'small',
    icon: TruckIcon,
    // Not a detector in the alarming sense — a vehicle crossing a line is traffic, not a
    // finding, so it wears the brand ink and never turns red.
    render: (s) => (
      <>
        <Figure value={n(s.events)} unit="crossings" />
        {hasSeries(s.series) && (
          <TodayPlot
            series={s.series}
            caption="Crossings per hour"
            scale={`max ${n(seriesMax(s.series))}`}
          >
            <MiniBars series={s.series} color={SERIES_BRAND} emphasis="peak" unit="crossings" />
          </TodayPlot>
        )}
        {s.events === 0 && <Quiet>No crossings today</Quiet>}
        <Sub items={[['Plates read', n(s.withPlate)]]} />
      </>
    ),
  },
  theft: {
    title: 'Theft',
    size: 'small',
    icon: HandRaisedIcon,
    render: detector({
      unit: 'detections',
      unitOne: 'detection',
      tone: 'var(--destructive)',
      extra: (s) => <Chips by={s.by} />,
    }),
  },
  /**
   * ── Fire & Smoke ──
   * An ordinary card now. It used to own a banner across the top of the row, on the reasoning
   * that fire is THE emergency — but a band belongs to severity rather than to one detector, and
   * a model with no card at all was the only one on the node whose day the dashboard didn't
   * report. (The band itself has since moved to the Alerts dashboard panel, which shows the open
   * alerts rather than a count of them.) Fire and smoke stay split: smoke without fire is the
   * early warning and the two call for different first moves.
   */
  'fire-smoke': {
    title: 'Fire & Smoke',
    size: 'small',
    icon: FireIcon,
    render: (s) => {
      const fire = Number(s.fire || 0);
      const smoke = Number(s.smoke || 0);
      const criticalAlerts = Number(s.alerts?.critical || 0);
      return (
        <>
          <Figure
            value={n(s.events)}
            unit={s.events === 1 ? 'detection' : 'detections'}
            tone={s.events > 0 ? 'var(--destructive)' : undefined}
          />
          {s.events > 0 && (
            <Sub
              items={[
                ['Fire', n(fire)],
                ['Smoke', n(smoke)],
                ...(criticalAlerts > 0
                  ? ([['Critical alerts', n(criticalAlerts)]] as Array<[string, ReactNode]>)
                  : []),
              ]}
            />
          )}
          {s.events === 0 && <Quiet>No fire or smoke detected today</Quiet>}
        </>
      );
    },
  },
  fall: {
    title: 'Falls',
    size: 'small',
    icon: ArrowTrendingDownIcon,
    render: detector({
      unit: 'detections',
      unitOne: 'detection',
      tone: 'var(--destructive)',
      extra: (s) => <Sub items={[['People involved', n(s.subjects)]]} />,
    }),
  },
};

/** The order cards appear in, regardless of what order the caller listed its surfaces. */
const CARD_ORDER = [
  'person-count',
  'face',
  'person',
  'intrusion',
  // Fire and smoke lead the detectors: of the models that report a finding rather than a
  // count, it is the one whose finding cannot wait.
  'fire-smoke',
  'ppe-violation',
  'vehicle-crossing',
  'theft',
  'fall',
];

// ─── Alert & presence card bodies ────────────────────────────────────────────

/**
 * The newest few alerts: severity, what tripped, where, and how long ago.
 *
 * This replaced a single "most recent" line and the sparkline that used to sit beside it in an
 * Alert Activity card of its own. A chart of alerts-per-hour says the day got busy at three and
 * nothing about what happened, which is the only part an operator can act on — and split across
 * two tiles, "how much is open" and "what just came in" each answered half a question. Four
 * rows is the whole answer, and it fits under the card that already holds the totals.
 *
 * WHERE is on the row because an alert name alone doesn't locate anything: three cameras raise
 * "Person Detected" and only the place tells the operator which one to look at. It renders only
 * when the camera is known — a dangling "at" is worse than no place at all.
 */
function RecentAlertList({ alerts, cameraNames }: { alerts: any[]; cameraNames?: Record<string, string> }) {
  return (
    <div className="mt-2.5 border-t border-border pt-2">
      <Caption>Latest alerts</Caption>
      <ul className="space-y-1.5">
        {alerts.length === 0 ? (
          <li className="text-[11px] text-muted-foreground">Nothing recent.</li>
        ) : (
          alerts.map((al: any, i: number) => {
            const where = al.camera_id ? cameraNames?.[al.camera_id] : undefined;
            return (
              <li key={al.id || i} className="flex items-center gap-1.5 text-[12px] leading-tight">
                <span
                  className="h-1.5 w-1.5 flex-none rounded-full"
                  style={{ backgroundColor: alertSeverityVar(al.severity) }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate" title={alertName(al)}>
                  <span className="font-medium text-foreground">{alertName(al)}</span>
                  {where && (
                    <>
                      <span className="text-muted-foreground"> at </span>
                      <span className="text-foreground">{where}</span>
                    </>
                  )}
                </span>
                <span className="flex-none tabular-nums text-muted-foreground">{formatAlertTime(al)}</span>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}

/** A clock reading from epoch seconds, or an em dash for a timestamp that isn't one. */
function clock(at?: number | null): string {
  if (!at) return '—';
  return new Date(at * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * Who arrived last: who they are, what they do, when they got in, and whether they are still
 * here.
 *
 * ── Why each of those is on the row ──
 *
 * The CLOCK READING, not an age. Ages ("2m") are right for alerts, which are worked in the order
 * they land; an arrival is checked against a shift start, and "09:58" is the form that answers
 * that question.
 *
 * The ROLE, because a name is not an identification. Three people called Sharma is the normal
 * case on a shift roster, and "Line Inspector" is the token that tells an operator which one
 * this is. It renders only when the record has one — an empty second line is worse than none.
 *
 * The STATE, because an arrival is not a presence. Someone who came at 07:00 and left at 09:30
 * belongs on this list and is NOT on site, and a row showing only their arrival time says the
 * opposite. Whoever is still here says so in words next to the time; whoever has gone shows the
 * hour they went, so the reader is never left inferring it from silence.
 */
function ArrivalList({ arrivals }: { arrivals: AiArrival[] }) {
  return (
    <div className="mt-2.5 border-t border-border pt-2">
      <Caption>Latest arrivals</Caption>
      <ul className="space-y-2">
        {arrivals.map((a, i) => (
          <li key={a.id || `${a.name}-${i}`} className="flex items-center gap-2">
            <Avatar name={a.name} className="h-6 w-6 text-[9px]" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[11px] font-medium leading-tight text-foreground" title={a.name}>
                {a.name}
              </p>
              {a.role && (
                <p className="truncate text-[10px] leading-tight text-muted-foreground" title={a.role}>
                  {a.role}
                </p>
              )}
            </div>
            <div className="flex-none text-right">
              <p className="text-[11px] font-medium leading-tight tabular-nums text-foreground">{clock(a.at)}</p>
              <p className="flex items-center justify-end gap-1 text-[9px] leading-tight text-muted-foreground">
                {a.inside ? (
                  <>
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: 'var(--success)' }}
                      aria-hidden
                    />
                    on site
                  </>
                ) : a.left_at ? (
                  `left ${clock(a.left_at)}`
                ) : (
                  'left'
                )}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Open alerts split by severity — and whatever is left over.
 *
 * The three named buckets are counted with `filter (where severity = …)`, so a row carrying a
 * severity outside that vocabulary lands in `total` and in none of them. Without the remainder
 * segment the bar would quietly rescale the three it knows about to fill the whole width, and
 * claim a hundred percent of the alerts were accounted for. It renders only when it is non-zero,
 * which on a healthy node is never.
 */
function severitySegments(open: AiAlertSummary['open']) {
  const other = Math.max(0, open.total - open.critical - open.warning - open.normal);
  return [
    { key: 'critical', label: 'Critical', value: open.critical, color: 'var(--destructive)' },
    { key: 'warning', label: 'Warning', value: open.warning, color: 'var(--warning)' },
    { key: 'normal', label: 'Info', value: open.normal, color: 'var(--info)' },
    ...(other > 0 ? [{ key: 'other', label: 'Other', value: other, color: SERIES_QUIET }] : []),
  ];
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AiAnalyticsCards({
  surfaces,
  summary,
  latestAlerts,
  cameraNames,
  loading = false,
  surfacesLoading,
  links = {},
  onNavigate,
  trailing,
  limit,
  className = '',
}: AiAnalyticsCardsProps) {
  const go = (to?: string) => (to && onNavigate ? () => onNavigate(to) : undefined);

  const active = surfaces || [];
  const cards = CARD_ORDER.filter((k) => active.includes(k) && SURFACE_CARDS[k]);

  const s = summary?.surfaces || {};

  const renderSurface = (key: string, compact: boolean) => {
    const card = SURFACE_CARDS[key];
    const data = s[key];
    return (
      <MiniCard
        key={key}
        title={card.title}
        icon={card.icon}
        onClick={go(links.reports?.[key])}
        loading={surfacesLoading ?? loading}
        compact={compact}
        className={compact ? 'flex-1' : ''}
      >
        {data == null ? (
          // Not zero — this model's query failed, and saying "0" would be a lie.
          <p className="text-[11px] text-muted-foreground">Couldn't read today's figures.</p>
        ) : (
          card.render(data, { cameraNames })
        )}
      </MiniCard>
    );
  };

  /**
   * Cards grouped into grid cells: one key for a big card's own cell, two for a stacked pair of
   * small ones.
   *
   * Small cards pair up in the order they appear, so a run of them fills cells two at a time. An
   * odd one at the end takes a cell of its own — its chart grows into the extra room, which is the
   * same thing that keeps a lone small card presentable anywhere else.
   *
   * A big card never pairs: it already carries a log, and stacking one would put four blocks of
   * reading in a cell built for two.
   */
  const surfaceCells: string[][] = [];
  for (let i = 0; i < cards.length; i += 1) {
    const [a, b] = [cards[i], cards[i + 1]];
    const pairable = SURFACE_CARDS[a]?.size === 'small' && b && SURFACE_CARDS[b]?.size === 'small';
    if (pairable) {
      surfaceCells.push([a, b]);
      i += 1;
    } else {
      surfaceCells.push([a]);
    }
  }

  const alerts = summary?.alerts;
  const attendance = summary?.attendance;
  const recent = latestAlerts || [];


  const nothingToShow =
    cards.length === 0 && !alerts && !attendance && recent.length === 0 && !trailing;


  /**
   * The cards as an ARRAY, which is what lets `limit` be a slice instead of a condition threaded
   * through every card. Order here IS the priority order the cap trims from the end of.
   *
   * Building the list eagerly costs nothing — each entry is an unrendered element, not a mounted
   * component — and anything the cap drops is never rendered at all.
   */
  const cells: ReactNode[] = [];

  /**
   * Alerts first: what needs a person, before what the cameras merely saw.
   *
   * ONE card, where there were two. "Alerts" held the standing position — how many are open,
   * split by severity — and "Alert Activity" held a per-hour sparkline with the newest few
   * alerts under it. Side by side they read as two answers to one question, and each was
   * missing the other's half: a count with no idea what it was made of, beside a chart whose
   * spikes you could see and not name. Merged, the card reads straight down as an operator
   * asks it — how bad is it, how much of that is today, what is it made of, and what just
   * came in.
   *
   * The per-hour chart is what the merge dropped. It was the one thing here that answered
   * "when", and the four dated rows underneath answer that well enough for a dashboard tile;
   * the alert list is where the shape of a day belongs.
   */
  if (alerts || recent.length > 0) {
    cells.push(
      <MiniCard
        key="alerts"
        title="Alerts"
        icon={BellAlertIcon}
        badge={alerts && alerts.open.unacked > 0 ? `${n(alerts.open.unacked)} new` : undefined}
        onClick={go(links.alerts)}
        loading={loading}
      >
        {alerts && (
          <>
            <Figure
              value={n(alerts.open.total)}
              unit="need attention"
              tone={alerts.open.critical > 0 ? 'var(--destructive)' : undefined}
              block
            />
            {/* Today's intake, against a headline that is a BACKLOG — an open count of
                thousands on a machine that raised six today is a retention story, not a
                night shift, and the two numbers only say that when they sit together.
                Critical is no longer a row of its own: the bar's legend below counts every
                severity, and it was the same number printed twice. */}
            <Sub items={[['Raised today', n(alerts.today.total)]]} />
            {alerts.open.total > 0 && (
              <div className="mt-2.5">
                <SegmentedBar segments={severitySegments(alerts.open)} />
              </div>
            )}
          </>
        )}
        {recent.length > 0 ? (
          <RecentAlertList alerts={recent.slice(0, 4)} cameraNames={cameraNames} />
        ) : (
          /* Nothing open AND nothing recent — the card is a zero and a heading, which in the
             tallest row on the page is mostly air. The well says the queue is clear. */
          <Quiet>No alerts need attention</Quiet>
        )}
      </MiniCard>
    );
  }

  if (attendance) {
    cells.push(
      <MiniCard key="presence" title="Presence" icon={UserGroupIcon} onClick={go(links.attendance)} loading={loading}>
        {/* `inside` is who is on site right now; `present` is everyone who came at all
            today. An employee who came and left is present but not inside — different
            questions, so the card answers both rather than picking one. */}
        <Figure value={n(attendance.inside)} unit="on site" tone={SERIES_BRAND} />
        <div className="mt-3">
          <Gauge
            percent={attendance.total > 0 ? (attendance.present / attendance.total) * 100 : 0}
            caption="present"
            color={SERIES_BRAND}
            ariaLabel={`${attendance.present} of ${attendance.total} employees present today`}
            legend={[
              {
                key: 'present',
                label: 'Present today',
                value: `${n(attendance.present)} / ${n(attendance.total)}`,
                color: SERIES_BRAND,
              },
              { key: 'absent', label: 'Absent', value: n(attendance.absent), color: SERIES_QUIET },
            ]}
          />
        </div>
        {attendance.arrivals && attendance.arrivals.length > 0 ? (
          <ArrivalList arrivals={attendance.arrivals} />
        ) : (
          /* Before the first shift lands this card is a ring at 0% and nothing else. Said
             plainly, because "no arrivals" at 09:00 is news and at 05:00 is not. */
          <Quiet>No arrivals recorded yet</Quiet>
        )}
      </MiniCard>
    );
  }

  for (const cell of surfaceCells) {
    cells.push(
      cell.length === 1 ? (
        renderSurface(cell[0], false)
      ) : (
        // One cell, two small cards. `flex-1` on each splits the cell, so the pair ends level with
        // the big card beside it and neither half is padded out.
        <div key={cell.join('+')} className="flex flex-col gap-3">
          {cell.map((key) => renderSurface(key, true))}
        </div>
      ),
    );
  }

  const shown = typeof limit === 'number' ? cells.slice(0, Math.max(0, limit)) : cells;

  /**
   * Columns follow the CELL COUNT, not the widest screen.
   *
   * A fixed four-column ladder is right for a node running eight models and wrong for one
   * running two: the two cards take a quarter of the row each and the rest is bare sheet, which
   * reads as cards that failed to load rather than as a short row. Stepping the ladder down to
   * what there is keeps the cards a sensible width and the row full at every count.
   * `trailing` counts for two, being a caller-supplied fragment of tiles rather than one.
   */
  const cellCount = shown.length + (trailing ? 2 : 0);
  const cols =
    cellCount >= 4
      ? 'sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4'
      : cellCount === 3
        ? 'sm:grid-cols-2 lg:grid-cols-3'
        : cellCount === 2
          ? 'sm:grid-cols-2'
          : '';


  return (
    <div className={`space-y-3 ${className}`}>
      {/* Stretched, so every tile in a row ends on the same line. What made the short cards look
          broken was never the stretch, it was where the leftover space went — MiniCard now
          spreads it between its own blocks instead of leaving it in a heap at the bottom. */}
      <div className={`grid grid-cols-1 gap-3 ${cols}`}>
        {shown}
        {trailing}
      </div>

      {surfaces === null && (
        <p className="text-[11px] text-muted-foreground">
          Model list unavailable — per-model figures are hidden until it can be read.
        </p>
      )}

      {nothingToShow && surfaces !== null && (
        <p className="text-[11px] text-muted-foreground">
          No AI models are attached to a camera on this node yet, so there is nothing to summarise.
        </p>
      )}
    </div>
  );
}
